export type SandboxImageId = 'medium' | 'large'

export interface SandboxImageResources {
  cpus: number
  ramMB: number
  storageGB: number
}

export interface SandboxImage {
  id: SandboxImageId
  name: string
  description: string
  snapshotName: string
  resources: SandboxImageResources
  meterFeatureId: string
}

export const DEFAULT_SANDBOX_IMAGE_ID: SandboxImageId = 'medium'
export const DEFAULT_SANDBOX_SNAPSHOT_VERSION = '0.5.0'

const sandboxImagesById: Record<SandboxImageId, SandboxImage> = {
  medium: {
    id: 'medium',
    name: 'Medium',
    description: '2 vCPU · 8 GB RAM · 10 GB disk',
    snapshotName: 'trevize-medium',
    resources: { cpus: 2, ramMB: 8192, storageGB: 10 },
    meterFeatureId: 'sandbox-runtime-medium-minute',
  },
  large: {
    id: 'large',
    name: 'Large',
    description: '2 vCPU · 16 GB RAM · 20 GB disk',
    snapshotName: 'trevize-large',
    resources: { cpus: 2, ramMB: 16384, storageGB: 20 },
    meterFeatureId: 'sandbox-runtime-large-minute',
  },
}

export const sandboxImages: readonly SandboxImage[] = [
  sandboxImagesById.medium,
  sandboxImagesById.large,
]

export function isSandboxImageId(value: string): value is SandboxImageId {
  return value === 'medium' || value === 'large'
}

export function getSandboxImage(id: SandboxImageId): SandboxImage {
  return sandboxImagesById[id]
}

export function resolveSandboxImageId(
  stored: string | null | undefined,
): SandboxImageId {
  return stored && isSandboxImageId(stored) ? stored : DEFAULT_SANDBOX_IMAGE_ID
}

export function snapshotVersionFromRef(
  snapshot: string | undefined,
): string | undefined {
  if (!snapshot) {
    return undefined
  }
  const colon = snapshot.lastIndexOf(':')
  if (colon <= 0) {
    return undefined
  }
  const version = snapshot.slice(colon + 1)
  return version.length > 0 ? version : undefined
}

export function sandboxSnapshotRef(
  imageId: SandboxImageId,
  version: string,
): string {
  return `${getSandboxImage(imageId).snapshotName}:${version}`
}

export function resolveSandboxSnapshot(input: {
  imageId: SandboxImageId
  snapshot?: string
}): string {
  if (input.imageId === DEFAULT_SANDBOX_IMAGE_ID && input.snapshot) {
    return input.snapshot
  }

  const version =
    snapshotVersionFromRef(input.snapshot) ?? DEFAULT_SANDBOX_SNAPSHOT_VERSION

  return sandboxSnapshotRef(input.imageId, version)
}
